import React, { useEffect, useState, useRef } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, ReloadOutlined, DollarOutlined, UserOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { Table, Empty, Tabs, Form, Divider, Skeleton, Button, message, Progress, Space, Popconfirm } from 'antd';
import { obtenerAcumuladosEmpleado, regenerarAcumulados, obtenerEmpleadosConRecibos } from '../../../../apis/ProcesoNominaApi';
import useCookies from "../../../../hooks/useCookies";
import FormBody from './form';

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: <>
      <DollarOutlined />
      <span>Nómina</span>
    </>,
  },
  {
    href: '',
    title: <>
      <UserOutlined />
      <span>Empleados</span>
    </>,
  },
  {
    href: '',
    title: <>
      <FolderOpenOutlined />
      <span>Acumulados por empleado</span>
    </>,
  }
];

const NominaCerrada = () => {
  const [empleadoCookie] = useCookies("empleado_id");
  const [form] = Form.useForm();
  const [result, setResult] = useState([]);
  const [resultAcumulados, setResultAcumulados] = useState([]);
  const [resultConecepts, setResultConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [disabledForm, setDisabled] = useState(true);
  const [header, setHeader] = useState(null);
  const [acumulado, setAcumulado] = useState([]);
  const bodyRef = useRef();
  const [activeTab, setActiveTab] = useState('1');

  const meses = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
  ];

  useEffect(() => {
    (async () => {
      if (empleadoCookie?.value > 0) {
        const _header = { ...header, odoo_empleado_id: empleadoCookie?.value };
        setHeader(_header);
      }
    })();
  }, [empleadoCookie]);

  useEffect(() => {
    if (acumulado.payload?.mensuales && acumulado.payload?.anuales) {
      console.log("Datos de la API:", acumulado.payload);
      const transformedData = transformData(acumulado.payload.mensuales, acumulado.payload.anuales);
      console.log("Datos transformados y ordenados:", transformedData);
      setResultConcepts(transformedData);
      setResultAcumulados(transformedData);
      setResult(transformedData);
    }
  }, [acumulado]);

  const handleOkEmpleado = async () => {
    setLoading(true);
    try {
        const values = await form.validateFields();
        if (!values) return;
        
        const empleado_id = values.odoo_empleado_id;
        const anio = values.anio;

        if (!empleado_id || !anio) {
            console.log("Empleado y Año son requeridos.");
            setLoading(false);
            return;
        }
        const acumulados = await obtenerAcumuladosEmpleado(empleado_id, anio);
        if (acumulados.ok) {
            setAcumulado(acumulados);
        }
    } catch (errorInfo) {
        console.log('Failed:', errorInfo);
    } finally {
        setLoading(false);
    }
};

  const handleRegenerarAcumulados = async () => {
    try {
      const values = await form.validateFields();
      if (!values) return;
      
      const empleado_id = values.odoo_empleado_id;
      const anio = values.anio;

      if (!empleado_id || !anio) {
        message.warning("Empleado y Año son requeridos para regenerar acumulados.");
        return;
      }

      setRegenerating(true);
      message.loading({ content: 'Regenerando acumulados del empleado...', key: 'regenerar', duration: 0 });

      // Enviar como array con un solo empleado
      const response = await regenerarAcumulados([empleado_id], anio);
      
      if (response.ok) {
        const { empleados_procesados, total_periodos_procesados, errores } = response.payload;
        
        if (errores && errores.length > 0) {
          message.error({ 
            content: `Error al regenerar acumulados: ${errores[0].error}`, 
            key: 'regenerar',
            duration: 5
          });
        } else {
          message.success({ 
            content: `Acumulados regenerados exitosamente. ${total_periodos_procesados} períodos procesados.`, 
            key: 'regenerar',
            duration: 3
          });
          
          // Recargar los acumulados sin recargar la página
          const acumulados = await obtenerAcumuladosEmpleado(empleado_id, anio);
          if (acumulados.ok) {
            setAcumulado(acumulados);
          }
        }
      } else {
        message.error({ 
          content: 'Error al regenerar acumulados', 
          key: 'regenerar',
          duration: 3
        });
      }
    } catch (error) {
      console.error('Error al regenerar acumulados:', error);
      message.error({ 
        content: 'Error al regenerar acumulados', 
        key: 'regenerar',
        duration: 3
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegenerarTodos = async () => {
    try {
      const values = await form.validateFields();
      if (!values) return;
      
      const anio = values.anio;

      if (!anio) {
        message.warning("Año es requerido para regenerar todos los acumulados.");
        return;
      }

      setRegenerating(true);
      message.loading({ content: 'Obteniendo empleados...', key: 'regenerar', duration: 0 });

      // Obtener todos los empleados con recibos en el año
      const empleadosResponse = await obtenerEmpleadosConRecibos(anio);
      
      if (!empleadosResponse.ok || !empleadosResponse.payload || empleadosResponse.payload.length === 0) {
        message.warning({ 
          content: 'No se encontraron empleados con recibos para este año', 
          key: 'regenerar',
          duration: 3
        });
        setRegenerating(false);
        return;
      }

      const empleadosIds = empleadosResponse.payload.map(emp => emp.id);
      
      message.loading({ 
        content: `Regenerando acumulados de ${empleadosIds.length} empleados...`, 
        key: 'regenerar', 
        duration: 0 
      });

      const response = await regenerarAcumulados(empleadosIds, anio);
      
      if (response.ok) {
        const { empleados_procesados, total_empleados, empleados_sin_recibos, total_periodos_procesados, errores } = response.payload;
        
        let messageContent = `Regeneración completada: ${empleados_procesados} de ${total_empleados} empleados procesados, ${total_periodos_procesados} períodos en total`;
        
        if (empleados_sin_recibos > 0) {
          messageContent += `. ${empleados_sin_recibos} sin recibos`;
        }
        
        if (errores && errores.length > 0) {
          messageContent += `. ${errores.length} errores`;
          message.warning({ 
            content: messageContent, 
            key: 'regenerar',
            duration: 5
          });
        } else {
          message.success({ 
            content: messageContent, 
            key: 'regenerar',
            duration: 5
          });
        }
        
        // Recargar los acumulados del empleado actual si hay uno seleccionado
        const empleado_id = form.getFieldValue('odoo_empleado_id');
        if (empleado_id) {
          const acumulados = await obtenerAcumuladosEmpleado(empleado_id, anio);
          if (acumulados.ok) {
            setAcumulado(acumulados);
          }
        }
      } else {
        message.error({ 
          content: 'Error al regenerar acumulados', 
          key: 'regenerar',
          duration: 3
        });
      }
    } catch (error) {
      console.error('Error al regenerar todos los acumulados:', error);
      message.error({ 
        content: 'Error al regenerar todos los acumulados', 
        key: 'regenerar',
        duration: 3
      });
    } finally {
      setRegenerating(false);
    }
  };


  const handleChangeEmpleado = async ({ }) => {
    try {
      const values = await form.validateFields();
      if (!values) return;
    } catch (errorInfo) {
      console.log('Failed:', errorInfo);
      return;
    }

    let success = false;
    const fields = form.getFieldsValue();
    const payload = {
      ...fields
    }
  }
  const transformData = (acumuladosMensuales, acumuladosAnuales) => {
    const conceptMap = new Map();
    
    console.log("transformData - Acumulados mensuales recibidos:", acumuladosMensuales);
    console.log("transformData - Acumulados anuales recibidos:", acumuladosAnuales);
  
    // Procesar los acumulados mensuales
    acumuladosMensuales.forEach((mes) => {
      if (mes.acumulados && Array.isArray(mes.acumulados)) {
        mes.acumulados.forEach((acumulado) => {
          // Crear key única: prefijo 'c_' para concepto_id, 'a_' para acumulado_id
          const conceptoId = acumulado.concepto_id ? `c_${acumulado.concepto_id}` : `a_${acumulado.acumulado_id}`;
          console.log("Mensual - concepto_id:", acumulado.concepto_id, "acumulado_id:", acumulado.acumulado_id, "conceptoId usado:", conceptoId, "clave:", acumulado.clave_concepto);
          if (!conceptMap.has(conceptoId)) {
            conceptMap.set(conceptoId, {
              concepto: {
                clave: acumulado.clave_concepto || acumulado.concepto?.clave || 'SIN CLAVE',
                descripcion: acumulado.concepto?.descripcion || acumulado.acumulado?.concepto?.descripcion,
                tipo_concepto: {
                  descripcion: acumulado.concepto?.tipo_concepto?.descripcion || acumulado.acumulado?.concepto?.tipo_concepto?.descripcion,
                },
              },
              enero: { dias: 0, horas: 0, exento: 0, monto: 0 },
              febrero: { dias: 0, horas: 0, exento: 0, monto: 0 },
              marzo: { dias: 0, horas: 0, exento: 0, monto: 0 },
              abril: { dias: 0, horas: 0, exento: 0, monto: 0 },
              mayo: { dias: 0, horas: 0, exento: 0, monto: 0 },
              junio: { dias: 0, horas: 0, exento: 0, monto: 0 },
              julio: { dias: 0, horas: 0, exento: 0, monto: 0 },
              agosto: { dias: 0, horas: 0, exento: 0, monto: 0 },
              septiembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
              octubre: { dias: 0, horas: 0, exento: 0, monto: 0 },
              noviembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
              diciembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
              totalAnual: {
                dias: 0,
                horas: 0,
                exento: 0,
                monto: 0,
              },
            });
          }
  
          const conceptoData = conceptMap.get(conceptoId);
          const mesNombre = meses[mes.num_mes - 1];
          conceptoData[mesNombre] = {
            dias: parseFloat(acumulado.dias || 0),
            horas: parseFloat(acumulado.horas || 0),
            exento: parseFloat(acumulado.exento || 0),
            monto: parseFloat(acumulado.monto || 0),
          };
        });
      }
    });
  
    acumuladosAnuales.forEach((acumuladoAnual) => {
      // Crear key única: prefijo 'c_' para concepto_id, 'a_' para acumulado_id
      const conceptoId = acumuladoAnual.concepto_id ? `c_${acumuladoAnual.concepto_id}` : `a_${acumuladoAnual.acumulado_id}`;
      console.log("Anual - concepto_id:", acumuladoAnual.concepto_id, "acumulado_id:", acumuladoAnual.acumulado_id, "conceptoId usado:", conceptoId, "clave:", acumuladoAnual.clave_concepto);
      if (!conceptMap.has(conceptoId)) {
        console.log("Creando nueva entrada para conceptoId:", conceptoId);
        conceptMap.set(conceptoId, {
          concepto: {
            clave: acumuladoAnual.clave_concepto || acumuladoAnual.concepto?.clave || 'SIN CLAVE',
            descripcion: acumuladoAnual.concepto?.descripcion || acumuladoAnual.acumulado?.concepto?.descripcion,
            tipo_concepto: {
              descripcion: acumuladoAnual.concepto?.tipo_concepto?.descripcion || acumuladoAnual.acumulado?.concepto?.tipo_concepto?.descripcion,
            },
          },
          enero: { dias: 0, horas: 0, exento: 0, monto: 0 },
          febrero: { dias: 0, horas: 0, exento: 0, monto: 0 },
          marzo: { dias: 0, horas: 0, exento: 0, monto: 0 },
          abril: { dias: 0, horas: 0, exento: 0, monto: 0 },
          mayo: { dias: 0, horas: 0, exento: 0, monto: 0 },
          junio: { dias: 0, horas: 0, exento: 0, monto: 0 },
          julio: { dias: 0, horas: 0, exento: 0, monto: 0 },
          agosto: { dias: 0, horas: 0, exento: 0, monto: 0 },
          septiembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
          octubre: { dias: 0, horas: 0, exento: 0, monto: 0 },
          noviembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
          diciembre: { dias: 0, horas: 0, exento: 0, monto: 0 },
          totalAnual: {
            dias: 0,
            horas: 0,
            exento: 0,
            monto: 0,
          },
        });
      }

      const conceptoData = conceptMap.get(conceptoId);
      conceptoData.totalAnual = {
        dias: parseFloat(acumuladoAnual.dias || 0),
        horas: parseFloat(acumuladoAnual.horas || 0),
        exento: parseFloat(acumuladoAnual.exento || 0),
        monto: parseFloat(acumuladoAnual.monto || 0),
      };
    });

    const transformedData = Array.from(conceptMap.values()).sort((a, b) =>
      (a.concepto?.clave || '').localeCompare(b.concepto?.clave || '')
    );
    
    console.log("transformData - Resultado final:", transformedData);
    console.log("transformData - Mapa final:", conceptMap);
    
    return (transformedData && Array.isArray(transformedData)) ? transformedData : [];
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'concepto',
      key: 'concepto',
      render: (item, object) => object.concepto?.tipo_concepto?.descripcion || '',
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      render: (item, object) => object.concepto?.clave || '',
    },
    {
      title: 'Descripción',
      dataIndex: 'concepto',
      key: 'concepto',
      render: (item, object) => object.concepto?.descripcion || '',
    },
    ...meses.map((mes) => ({
      title: mes.slice(0, 3).toUpperCase(),
      dataIndex: mes,
      key: mes,
      render: (value) => value.monto.toFixed(2),
    })),
  ];

  const sortedData = (resultConecepts || []).sort((a, b) => {
    const claveA = a.concepto?.clave || '';
    const claveB = b.concepto?.clave || '';
    return claveA.localeCompare(claveB);
  });

  const getColumns = (activeTab) => {
    const baseColumns = [
      {
        title: 'Tipo',
        dataIndex: 'concepto',
        key: 'concepto',
        render: (item, object) => object.concepto?.tipo_concepto?.descripcion || '',
      },
      {
        title: 'Concepto',
        dataIndex: 'concepto',
        key: 'concepto',
        render: (item, object) => object.concepto?.clave || '',
      },
      {
        title: 'Descripción',
        dataIndex: 'concepto',
        key: 'concepto',
        render: (item, object) => object.concepto?.descripcion || '',
      },
    ];
  
    const fieldMap = {
      '1': 'dias',   // Pestaña "Dias"
      '2': 'exento', // Pestaña "Excento"
      '3': 'horas',  // Pestaña "Horas"
      '4': 'monto',  // Pestaña "Monto"
    };
  
    const field = fieldMap[activeTab];
  
    const monthColumns = meses.map((mes) => ({
      title: mes.slice(0, 3).toUpperCase(),
      dataIndex: mes,
      key: mes,
      render: (value) => value[field].toFixed(2),
    }));
  
    const totalAnualColumn = {
      title: 'Total Anual',
      key: 'totalAnual',
      render: (_, record) => record.totalAnual[field].toFixed(2),
    };
  
    return [...baseColumns, ...monthColumns, totalAnualColumn];
  };


  return (
    <PageApp routes={routes} title={'Consulta acumulados por empleado'}>
      <Divider />
      <FormBody
        header={header}
        ref={bodyRef}
        onOk={handleOkEmpleado}
        onChangeEmpleado={handleChangeEmpleado}
        form={form}
        disabledForm={disabledForm}
      />
      <Divider />
      {(form.getFieldValue('odoo_empleado_id') && form.getFieldValue('anio')) && (
        <Space style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={handleRegenerarAcumulados}
            loading={regenerating}
            disabled={loading || regenerating}
          >
            Regenerar Acumulados
          </Button>
          <Popconfirm
            title="Regenerar todos los acumulados"
            description={`¿Está seguro de regenerar los acumulados de TODOS los empleados del año ${form.getFieldValue('anio')}? Este proceso puede tardar.`}
            onConfirm={handleRegenerarTodos}
            okText="Sí, regenerar todos"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            disabled={loading || regenerating}
          >
            <Button 
              type="default" 
              danger
              icon={<ReloadOutlined />} 
              loading={regenerating}
              disabled={loading || regenerating}
            >
              Regenerar TODOS
            </Button>
          </Popconfirm>
          {regenerating && (
            <span style={{ marginLeft: 8, color: '#1890ff' }}>
              Procesando...
            </span>
          )}
        </Space>
      )}
      {
  loading ? (
    <Skeleton active={loading} />
  ) : (
    <>
      {(result || []).length === 0 ? (
        <Empty />
      ) : (
        <Tabs
          defaultActiveKey="1"
          onChange={(key) => setActiveTab(key)}
          items={[
            {
              key: '1',
              label: 'Dias',
              children: (
                <Table
                  dataSource={(resultConecepts || []).sort((a, b) => (a.concepto?.clave || '').localeCompare(b.concepto?.clave || ''))}
                  columns={getColumns(activeTab)}
                  pagination={false}
                />
              ),
            },
            {
              key: '2',
              label: 'Exento',
              children: (
                <Table
                  dataSource={(resultAcumulados || []).sort((a, b) => (a.concepto?.clave || '').localeCompare(b.concepto?.clave || ''))}
                  columns={getColumns(activeTab)}
                  pagination={false}
                />
              ),
            },
            {
              key: '3',
              label: 'Horas',
              children: (
                <Table
                  dataSource={(resultConecepts || []).sort((a, b) => (a.concepto?.clave || '').localeCompare(b.concepto?.clave || ''))}
                  columns={getColumns(activeTab)}
                  pagination={false}
                />
              ),
            },
            {
              key: '4',
              label: 'Monto',
              children: (
                <Table
                  dataSource={(resultAcumulados || []).sort((a, b) => (a.concepto?.clave || '').localeCompare(b.concepto?.clave || ''))}
                  columns={getColumns(activeTab)}
                  pagination={false}
                />
              ),
            },
          ]}
        />
      )}
    </>
  )
}
    </PageApp>
  );
};

export default NominaCerrada;